import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { StatusSection } from '../StatusSection'
import { WatchlistSearch } from './WatchlistSearch'
import { WatchlistFilter } from './WatchlistFilter'
import { WatchlistFilterMobile } from './WatchlistFilterMobile'
import { ReviewPopup } from '../../ReviewSystem/ReviewPopup'
import { useWatchlistActions } from '../../../hooks/useWatchlistActions'
import { loadList } from '../../../store/slices/watchlist.slice'

const STATUSES = [
  { value: 'Watching', label: 'כרגע צופה' },
  { value: 'Plan to Watch', label: 'מתכנן לצפות' },
  { value: 'Completed', label: 'סיימתי לצפות' },
  { value: 'On-Hold', label: 'בהמתנה' },
  { value: 'Dropped', label: 'ננטש' },
]

export function UserWatchlist({ userId }) {
  const dispatch = useDispatch()

  const {
    enrichedAll = [],
    enrichedFiltered = [],
    filterText,
    setFilterText,
    filterStatus,
    setFilterStatus,
    searchTerm,
    setSearchTerm,
    searchResults,
    statusToAdd,
    setStatusToAdd,
    handleSearch,
    handleAddItem,
    handleIncrement,
    handleDecrement,
    handleRemoveItem,
    handleSaveChanges,
    getUpdatedWatchItem,
    reviewOpen,
    reviewAnime,
    setReviewOpen,
    loggedInUser,
    canEdit,
  } = useWatchlistActions(userId)

  // ❶ ברגע שיש userId – טען את הרשימה מהשרת
  useEffect(() => {
    if (!userId) return
    dispatch(
      loadList({
        userId,
        listType: 'watchlist',
        cacheKey: 'all', // ← bucket כללי
        options: { page: 1, limit: 10_000 },
      })
    )
  }, [dispatch, userId])

  // ❷ חישוב סך כמות לכל סטטוס
  const totalPerStatus = useMemo(() => {
    return enrichedAll.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1
      return acc
    }, {})
  }, [enrichedAll])

  // ❸ בחירת רשימה להצגה לפי סטטוס/חיפוש
  const listSource = useMemo(() => {
    if (filterStatus) return enrichedFiltered
    return enrichedAll
  }, [filterStatus, enrichedFiltered, enrichedAll])

  // ❹ פילטור לפי סטטוס וטקסט חיפוש
  const byStatus = (statusVal) => {
    return listSource.filter((item) => {
      if (item.status !== statusVal) return false
      if (!filterText) return true
      const q = filterText.toLowerCase()
      return (
        (item.title_hebrew && item.title_hebrew.includes(filterText)) ||
        (item.title_english && item.title_english.toLowerCase().includes(q))
      )
    })
  }

  return (
    <div className="mx-auto p-4">
      {/* ── Add/Search Modal ───────────────────────────── */}
      {canEdit && statusToAdd && (
        <WatchlistSearch
          statuses={STATUSES}
          currentStatusToAdd={statusToAdd}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          searchResults={searchResults}
          handleSearch={handleSearch}
          handleAddItem={handleAddItem}
          watchlist={enrichedAll}
        />
      )}

      <div className="mt-10 container mx-auto">
        <div className="flex flex-col gap-6 md:grid md:grid-cols-[30%_70%] xl:grid-cols-[20%_80%]">
          {/* ── Desktop filter ───────────────────────────── */}
          <aside className="hidden md:block">
            <WatchlistFilter
              filterText={filterText}
              setFilterText={setFilterText}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              watchlist={enrichedAll}
              statuses={STATUSES}
            />
          </aside>

          {/* ── Mobile filter ───────────────────────────── */}
          <WatchlistFilterMobile
            filterText={filterText}
            setFilterText={setFilterText}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            watchlist={enrichedAll}
            statuses={STATUSES}
          />

          {/* ── Lists by status ───────────────────────────── */}
          <main>
            <div className="flex flex-col gap-8">
              {(filterStatus ? STATUSES.filter((s) => s.value === filterStatus) : STATUSES).map(({ value, label }) => {
                const filteredItems = byStatus(value)
                if (!filteredItems.length) return null

                return (
                  <StatusSection
                    key={value}
                    statusTitle={label}
                    statusValue={value}
                    totalCount={totalPerStatus[value] || 0}
                    items={filteredItems}
                    fullListLength={enrichedAll.length}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    onAdd={setStatusToAdd}
                    onRemove={handleRemoveItem}
                    onSaveChanges={handleSaveChanges}
                    getUpdatedWatchItem={getUpdatedWatchItem}
                    canEdit={canEdit}
                  />
                )
              })}
            </div>
          </main>
        </div>
      </div>

      {/* ── Review popup ───────────────────────────────── */}
      {reviewOpen && reviewAnime && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]"
          onClick={() => setReviewOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <ReviewPopup
              initialData={null}
              animeId={reviewAnime.anizone_id}
              loggedInUser={loggedInUser}
              onClose={() => setReviewOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  )
}
